// RoleGuard component for InfluenceHub MVP
// Protects routes that require authentication and profile data
// Uses tri-state model: loading, authenticated, unauthorized
// Does NOT aggressively sign out users - only redirects
//
// NOTE: Approval-based gating has been removed.
// All authenticated users in allowedRoles can access guarded routes.
// Status fields (e.g. 'pending_approval') are now ignored at the app level.

import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
const RoleGuard = ({ children, onNavigate, allowedRoles, requireApproved = true }) => {
  const { user, profile, loading } = useAuth();

  const effectiveAllowedRoles = allowedRoles || ['brand', 'influencer', 'creator', 'admin'];

  // Handle redirects based on auth state
  useEffect(() => {
    // Don't do anything while loading
    if (loading) {
      return;
    }

    // Not logged in at all - redirect to login
    if (!user) {
      onNavigate('login');
      return;
    }

    // Authenticated but no Firestore user doc yet (or snapshot error): send to role gate, not signup/profile.
    if (!profile) {
      if (!loading) {
        onNavigate('/select-role', null, { replace: true });
      }
      return;
    }

    // User has profile - check role (approval status is no longer checked)
    let userRole = profile.userType || profile.role;

    if (!userRole) {
      onNavigate('/select-role', null, { replace: true });
      return;
    }

    const isCreator = userRole === 'creator' || userRole === 'influencer';
    const allowed = effectiveAllowedRoles.includes(userRole) || (isCreator && effectiveAllowedRoles.some((r) => r === 'influencer' || r === 'creator'));
    if (!allowed) {
      if (userRole === 'brand') {
        onNavigate('/brand/dashboard');
      } else {
        onNavigate('/profile/edit');
      }
      return;
    }

    // NOTE: Approval-based blocking has been removed.
    // All authenticated users with valid roles now have immediate access.
    // The requireApproved prop is kept for backwards compatibility but is a no-op.
  }, [user, profile, loading, onNavigate, effectiveAllowedRoles]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    // Navigation handled by useEffect
    return null;
  }

  if (!profile) {
    if (!loading) {
      onNavigate('/select-role', null, { replace: true });
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-slate-600">Redirecting...</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  let userRole = profile.userType || profile.role;
  if (!userRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-slate-600">Redirecting...</p>
      </div>
    );
  }
  const isCreator = userRole === 'creator' || userRole === 'influencer';
  const allowed = effectiveAllowedRoles.includes(userRole) || (isCreator && effectiveAllowedRoles.some((r) => r === 'influencer' || r === 'creator'));

  if (!allowed) {
    return null;
  }

  // NOTE: Approval-based blocking has been removed.
  // All authenticated users with valid roles now have immediate access.

  // All checks passed - render protected content
  return <>{children}</>;
};

export default RoleGuard;
